/**
 * Which routes render with no navigation strip and no sidebar (issue
 * #730/#754), and the per-route flag that decides it.
 *
 * Kept in its own pure-JS module, rather than inline in `components/
 * Layout.jsx`, specifically so it is unit-testable directly: `Layout.jsx`
 * imports `react-router-dom`, which resolves to an ESM-only build
 * (`node_modules/react-router-dom/dist/index.mjs`) that Jest's default CJS
 * resolver cannot `require` — a test importing `Layout.jsx` itself (rather
 * than reading it as text) fails with "Cannot find module 'react-router-
 * dom'" before it gets anywhere near the logic under test. This module has
 * no such import, so `Layout.chromeless.test.js` can call
 * `isChromelessRoute` directly, the same way `featureFlags.test.js` calls
 * `newUi()` directly.
 */
import { newUi } from "./featureFlags";

/**
 * The set-up walkthrough (and, in the same wave, the coach's own first
 * run) has nowhere to navigate to yet — every destination in `Layout.jsx`'s
 * `PLAYER_NAV`/`COACH_NAV` is an empty page for an account seconds old.
 * `/coach-setup` is listed here now, before its own screen exists,
 * specifically so that sub-issue (#755) does not have to re-edit
 * `Layout.jsx` — a hot file, one stream per wave (see #749's wave table).
 */
export const CHROMELESS_ROUTES = ["/onboarding", "/coach-setup"];

/**
 * PR #777 review, F4. Each chromeless route is gated on ITS OWN `newUi(...)`
 * screen — never on one shared flag. `NEW_UI_SCREENS` (`featureFlags.js`)
 * carries `"setup"` and `"coach-setup"` as separate ids, and one-flag-per-
 * screen is the whole of #751's contract: gating `/coach-setup` on
 * `newUi("setup")` (this file's earlier, single-flag version) would have
 * meant a tester flagging only `coach-setup` got the new coach screen with
 * the nav strip still under it, and a tester flagging only `setup` stripped
 * chrome off a route this slice does not own.
 *
 * Every `newUi(...)` argument below is a string literal, never a lookup by
 * `pathname` into a `{route: screen}` map — `newUiScreenContract.test.js`'s
 * R7 scans `frontend/src` for exactly that computed-argument shape and
 * refuses it outright, because a source scan cannot verify a variable's
 * runtime value. Its own docstring names this precise anti-pattern.
 */
export function isChromelessRoute(pathname) {
  if (pathname === "/onboarding") return newUi("setup");
  if (pathname === "/coach-setup") return newUi("coach-setup");
  return false;
}

/**
 * Issue #797, Andreas's decision 2026-09-23: retire the top bar's text-size
 * control ("Aa", `AccessibilityMenu.jsx`) on a rebuilt screen — the kit's
 * type ramp is fixed px there (#792/#826), so the control already moves
 * nothing; browser zoom is the accessibility path on those screens instead.
 * High contrast is UNTOUCHED — it is a different feature that still works
 * everywhere, including on a rebuilt screen — so this function answers only
 * "is the PAGE currently shown a rebuilt screen with its own flag on",
 * never "hide the whole accessibility menu".
 *
 * FIX ROUND (review, B1/B2): retiring the control is a CLAIM about the
 * rendered screen ("this page's marked content does not move at Compact,
 * Default or Large"), and that claim must be proven, per screen, by
 * `frontend/e2e/text-size-tokens.spec.js` before this function may answer
 * `true` for it — never assumed from "this route is chromeless" the way
 * the PR's first cut did.
 *
 * `/onboarding` and `/coach-setup` FAILED THE CHECK when B1/B2 landed — both
 * are chromeless routes (`isChromelessRoute`, reused below), but neither's
 * own marked content was provably fixed px. `/onboarding` embedded the
 * shared `PlayerProfile` form, whose inputs and labels were still Tailwind
 * `rem` (17→19px at Large) — the SAME component Settings and the shipped
 * `Onboarding.jsx` render, so fixing it in place would have silently
 * changed two screens #792/#797 were never asked to touch. `/coach-setup`
 * embedded `AddPlayerCard` via `components/ui/button.jsx`'s/`ui/input.jsx`'s
 * own base classes — every `<Button>`/`<Input>` in the app, not a
 * `/coach-setup`-only leftover. Both were filed as #830 rather than fixed
 * there.
 *
 * #830's fix: a `kitType` prop on each shared component (default `false`,
 * unchanged rendering — `PlayerProfile.jsx`/`AddPlayerCard.jsx`'s own
 * docstrings), set ONLY by the two callers below, so Settings, the shipped
 * `Onboarding.jsx` and `CoachPortal.jsx` are untouched. With that in place
 * BOTH routes now pass `text-size-tokens.spec.js`'s live check (zero moving
 * elements — see that file's own two `describe` blocks; PR review F1
 * fix round also closed the gap where a portalled `<Select>`'s own open
 * option list wasn't reachable by that check at all). So both routes now
 * DELEGATE to `isChromelessRoute` — the same "chromeless ⇒
 * text-size-retired" fact that was true by coincidence before #830 and is
 * now true because the underlying TYPE claim is proven, not assumed.
 * THIS function's own CONTROL claim (that flipping it to `true` actually
 * hides the top-bar trigger) is watched failing/passing separately, with
 * THIS flip reverted — not in `text-size-tokens.spec.js` (that file never
 * touches `hidesTextSizeControl` or the top bar at all) but in
 * `components/Layout.chromeless.test.js`'s own `hidesTextSizeControl`
 * describe block, and live, both legs, in
 * `e2e/journeys/31-text-size-control-retirement.journey.spec.js` (PR review
 * F5 — see that journey's own header for the fault it was injected
 * against on each leg). This is still a SEPARATE decision from
 * `isChromelessRoute`, not a rename of it: a THIRD rebuilt screen that is
 * chromeless but whose own type still moves must be added here
 * explicitly, backed by its own passing check, never picked up for free
 * just by being chromeless.
 *
 * `/coach-portal` is INTENTIONALLY ABSENT from this function — not merely
 * "not yet passing", but the wrong SHAPE of check for that route. The same
 * path serves two compositions (the shipped, populated list and the
 * redrawn empty state) that a route+flag pair cannot tell apart; see
 * `context/RetireTextSizeContext.js` and `Layout.jsx`'s own `hideTextSize`
 * computation for the mechanism that replaces it.
 *
 * `/register` (SignUpNew) needs no entry here: it renders OUTSIDE
 * `<Layout/>` entirely (`App.js`'s top-level `/register` route is a
 * sibling of the `<Layout/>` element, not nested under it), so neither
 * flag state has ever put the "Aa" control there — nothing to hide, and
 * nothing to measure.
 */
export function hidesTextSizeControl(pathname) {
  if (pathname === "/onboarding") return isChromelessRoute(pathname);
  if (pathname === "/coach-setup") return isChromelessRoute(pathname);
  return false;
}
